// This Pendo script injection implementation is based on the approach found in the ngx-pendo library
// https://github.com/yociduo/ngx-pendo/

import { inject, InjectionToken, isDevMode, provideAppInitializer } from '@angular/core';
import { environment } from '@environments/environment';
import { IPendoSettings } from '@types';
import { interval } from 'rxjs';

/** Angular way of accessing the global `pendo` object created by the Pendo script after initialization */
// eslint-disable-next-line no-var
declare var pendo: any;

/** Injection token (only exported for unit testing) */
export const PENDO_SETTINGS_TOKEN = new InjectionToken<IPendoSettings>('ngx-pendo-settings', {
    factory: () => ({
        pendoApiKey: '',
        pendoIdFormatter: (value: any) => value.toString().toLowerCase(),
    }),
});

/**
 * Provides the application with Pendo, the user journey analytics tooling used by CS.
 * @returns app initializer providers
 */
export const providePendoAnalytics = (): any[] => [
    {
        provide: PENDO_SETTINGS_TOKEN,
        useValue: {
            pendoApiKey: environment.pendo.apiKey,
        },
    },
    provideAppInitializer(() => {
        const initializerFn = pendoInitializer(inject(PENDO_SETTINGS_TOKEN));
        return initializerFn();
    }),
];

export function pendoInitializer($settings: IPendoSettings): () => Promise<void> {
    return async () => {
        if (!$settings.pendoApiKey) {
            if (isDevMode()) {
                console.error('Empty api key for Pendo. Make sure to provide one when initializing NgxPendoModule.');
            }

            return;
        }

        await new Promise<void>((resolve, _) => {
            const script = document.createElement('script');
            script.async = true;

            script.src = `https://cdn.pendo.io/agent/static/${$settings.pendoApiKey}/pendo.js`;

            script.onerror = async () => {
                // The script may have been blocked by an ad blocker
                resolve();
            };
            script.onload = async () => {
                // when enableDebugging should load extra js
                const sub = interval(100).subscribe(() => {
                    if (pendo) {
                        sub.unsubscribe();
                        resolve();
                    }
                });
            };
            document.head.appendChild(script);
            console.debug('[Injectors][PendoInjector] Successfully injected.');
        });
    };
}
